import React, { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import FloatingElements from './FloatingElements';

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });

  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.9, 1, 0.95]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0.5]);

  const text = "We are a web design and development company, building websites that drive traffic, engagement, and conversion for industry-leading brands and startups in Dubai.";
  const words = text.split(' ');

  return (
    <section id="about" ref={ref} className="bg-[#1a1a1a] py-16 sm:py-20 md:py-24 lg:py-32 px-4 sm:px-6 md:px-12 relative overflow-hidden">
      {/* Floating elements */}
      <FloatingElements />

      {/* Animated background elements - optimized */}
      <motion.div
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.02, 0.03, 0.02]
        }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-1/4 left-1/4 w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 bg-orange-500 rounded-full blur-[80px] md:blur-[100px] pointer-events-none will-change-transform"
      />
      <motion.div
        animate={{
          scale: [1.1, 1, 1.1],
          opacity: [0.03, 0.02, 0.03]
        }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 5 }}
        className="absolute bottom-1/4 right-1/4 w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 bg-orange-500 rounded-full blur-[80px] md:blur-[100px] pointer-events-none will-change-transform"
      />

      {/* Decorative lines */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={isInView ? { scaleX: 1 } : {}}
        transition={{ duration: 1.2, delay: 0.4 }}
        className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-orange-500/50 to-transparent origin-left"
      />
      <motion.div
        initial={{ scaleX: 0 }}
        animate={isInView ? { scaleX: 1 } : {}}
        transition={{ duration: 1.2, delay: 0.6 }}
        className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-orange-500/50 to-transparent origin-right"
      />

      <motion.div
        style={{ scale, opacity }}
        className="max-w-5xl mx-auto text-center relative z-10"
      >
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-orange-500 text-xs sm:text-sm md:text-base tracking-[0.3em] sm:tracking-[0.4em] mb-8 sm:mb-10 md:mb-12 font-light relative"
        >
          A DIGITAL AGENCY
        </motion.h2>

        <motion.p className="text-white text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl leading-relaxed font-light">
          {words.map((word, index) => (
            <motion.span
              key={index}
              className="inline-block mr-2 sm:mr-3 will-change-transform"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{
                duration: 0.4,
                delay: 0.2 + index * 0.03,
                ease: [0.6, 0.05, 0.01, 0.9]
              }}
            >
              {word}
            </motion.span>
          ))}
        </motion.p>

        {/* Decorative quote marks */}
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={isInView ? { opacity: 0.1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="absolute -top-4 sm:-top-6 md:-top-8 -left-2 sm:-left-4 text-5xl sm:text-7xl md:text-9xl text-orange-500 font-serif pointer-events-none"
        >
          "
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={isInView ? { opacity: 0.1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 1 }}
          className="absolute -bottom-4 sm:-bottom-6 md:-bottom-8 -right-2 sm:-right-4 text-5xl sm:text-7xl md:text-9xl text-orange-500 font-serif rotate-180 pointer-events-none"
        >
          "
        </motion.div>
      </motion.div>
    </section>
  );
};

export default About;
